import React, {useEffect} from 'react';
import { CartFilled, CartRegular } from '@fluentui/react-icons';
import { CompoundButton, CounterBadge } from '@fluentui/react-components';
import { useGetCartQuery } from '../../Api/Api';
import { useNavigate } from 'react-router-dom';
import {useGetPreparedCartQuery} from "../../Api/PreparedCartEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../../Store/Store";
import {CART_DRAG_MIME, ICartItemIdentity, useAddToCartAction} from "./useAddToCartAction";
import {readableOn} from "../Helpers/ColorHelpers";

interface ICartIconProps {
    fontColor?: string;
    // When provided (merchant enabled cartPageConfig.openInPanel) and no prepared cart is active,
    // clicking opens the slide-out cart panel instead of navigating to the full /cart page.
    onOpenPanel?: () => void;
}

const CartIcon: React.FC<ICartIconProps> = (props) => {
    const settings = useSelector((state: RootState) => state.cart);
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const cart = useGetCartQuery();
    const navigate = useNavigate();
    const addToCartAction = useAddToCartAction();

    const [cartFull, setCartFull] = React.useState(false);
    const [dragOver, setDragOver] = React.useState(false);

    useEffect(() => {
        setCartFull(false);
        if(cart.data?.products && cart.data?.products.length > 0){
            setCartFull(true);
        }
        else if(getPreparedCart.data?.products && getPreparedCart.data?.products.length > 0 || getPreparedCart.data?.bundles && getPreparedCart.data?.bundles.length > 0){
            setCartFull(true);
        }
    }, [cart.data, getPreparedCart.data]);

    // Count from the same cart the cartFull logic reflects: the prepared cart when one is
    // selected, otherwise the regular cart. Sum item quantities across products + bundles.
    const activeCart = settings.selectedCartId ? getPreparedCart.data : cart.data;
    const cartCount = (activeCart?.products?.reduce((acc, p) => acc + p.quantity, 0) ?? 0) +
        (activeCart?.bundles?.reduce((acc, b) => acc + b.quantity, 0) ?? 0);
    // Badge palette follows the merchant header bar: fill = the (contrast-checked) fontColor,
    // text = a legible color on that fill. When fontColor is unset, fall back to a neutral red.
    const badgeBg = props.fontColor ?? '#d13438';
    const badgeColor = readableOn(badgeBg);

    const click = () => {
        if(settings.selectedCartId){
            navigate(`/prepared-cart/${settings.selectedCartId}`);
        }
        else if(props.onOpenPanel){
            props.onOpenPanel();
        }
        else {
            navigate(`/cart`);
        }
    }

    const dragHasCartItem = (e: React.DragEvent) => e.dataTransfer.types.includes(CART_DRAG_MIME);

    const onDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const raw = e.dataTransfer.getData(CART_DRAG_MIME);
        if (!raw) return;
        let item: ICartItemIdentity;
        try { item = JSON.parse(raw); } catch { return; }
        const added = await addToCartAction(item);
        // Surface the result: pop the panel open if the storefront uses one, else the icon fills
        // via the cart query update.
        if (added && props.onOpenPanel && !settings.selectedCartId) {
            props.onOpenPanel();
        }
    };

    return (
        <div
            onDragOver={(e) => { if (dragHasCartItem(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!dragOver) setDragOver(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
            onDrop={onDrop}
            style={{
                position: 'relative',
                borderRadius: 8,
                transition: 'box-shadow 0.15s, background-color 0.15s',
                outline: dragOver ? `2px dashed ${props.fontColor ?? 'currentColor'}` : undefined,
                outlineOffset: 2,
                backgroundColor: dragOver ? 'rgba(127,127,127,0.18)' : undefined,
            }}
            title={dragOver ? 'Drop to add to cart' : undefined}
        >
            <CompoundButton
                appearance='transparent'
                icon={cartFull ? <CartFilled style={{color: props.fontColor}} /> : <CartRegular style={{color: props.fontColor}} />}
                onClick={click}
                >
            </CompoundButton>
            {cartCount > 0 &&
                <CounterBadge
                    count={cartCount}
                    overflowCount={99}
                    size='small'
                    // Not color='brand' — the badge must sit on the merchant header palette. Fill and
                    // text come from the (contrast-checked) header fontColor via inline style.
                    style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: badgeBg,
                        color: badgeColor,
                        // Never intercept clicks/drag — leaves the button + drop behavior untouched.
                        pointerEvents: 'none',
                    }}
                />
            }
        </div>
    )
}

export default CartIcon;